/**
 * NavigationContext - контекст для отслеживания способа навигации
 *
 * Определяет был ли переход:
 * - direct: прямой вход по ссылке (печать URL, обновление страницы, ссылка извне)
 * - programmatic: программная навигация (клик по кнопке/ссылке внутри приложения)
 * 
 * ОПТИМІЗАЦІЯ:
 * - Використовуємо useRef замість useState для navigationState
 * - Це запобігає перерендеру всіх дочірніх компонентів при зміні стану навігації
 */

import React, { createContext, useContext, useEffect, useRef, useMemo, useCallback, ReactNode } from 'react'
import { setNavigationContextForTasks } from '@/Core/Tasks/Resources/OpenResourceCardTask'

interface NavigationState {
    type: 'direct' | 'programmatic'
    fromUrl?: string | undefined
    timestamp: number
}

interface NavigationContextType {
    markProgrammaticNavigation: (fromUrl?: string) => void
    resetToDirectNavigation: () => void
    isProgrammaticNavigation: () => boolean
}

const NavigationContext = createContext<NavigationContextType | null>(null)

export const useNavigationContext = (): NavigationContextType => {
    const context = useContext(NavigationContext)
    if (!context) {
        throw new Error('useNavigationContext must be used within NavigationProvider')
    }
    return context
}

interface NavigationProviderProps {
    children: ReactNode
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
    // Використовуємо useRef замість useState, щоб зміни не викликали перерендер
    const navigationStateRef = useRef<NavigationState>({
        type: 'direct',
        timestamp: Date.now(),
    })

    useEffect(() => {
        // При первой загрузке всегда считаем прямым входом
        navigationStateRef.current = {
            type: 'direct',
            timestamp: Date.now(),
        }

        // Слушаем события браузера для определения типа навигации
        const handlePopState = () => {
            // При использовании back/forward всегда считаем прямым входом
            navigationStateRef.current = {
                type: 'direct',
                timestamp: Date.now(),
            }
        }

        if (typeof window !== 'undefined') {
            window.addEventListener('popstate', handlePopState)
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('popstate', handlePopState)
            }
        }
    }, [])

    const markProgrammaticNavigation = useCallback((fromUrl?: string) => {
        navigationStateRef.current = {
            type: 'programmatic',
            fromUrl:
                fromUrl || (typeof window !== 'undefined' ? window.location.pathname : undefined),
            timestamp: Date.now(),
        }
    }, [])

    const resetToDirectNavigation = useCallback(() => {
        navigationStateRef.current = {
            type: 'direct',
            timestamp: Date.now(),
        }
    }, [])

    const isProgrammaticNavigation = useCallback((): boolean => {
        // Считаем навигацию программной только если она была отмечена в последние 100мс
        // Это исключает случаи когда пользователь обновил страницу или ввел URL
        const state = navigationStateRef.current
        const timeDiff = Date.now() - state.timestamp
        const result = state.type === 'programmatic' && timeDiff < 100

        return result
    }, [])

    // Мемоізуємо contextValue, щоб він не змінювався при кожному рендері
    const contextValue = useMemo<NavigationContextType>(() => ({
        markProgrammaticNavigation,
        resetToDirectNavigation,
        isProgrammaticNavigation,
    }), [markProgrammaticNavigation, resetToDirectNavigation, isProgrammaticNavigation])

    // Инициализируем контекст для использования в задачах (вне React компонентов)
    useEffect(() => {
        setNavigationContextForTasks(contextValue)
    }, [contextValue])

    return <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>
}
